import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import commissionService from '../../services/ShipmentCommissionService.js';
import commissionPaymentService from '../../services/ShipmentCommissionPaymentService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { DollarSign, MapPin, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { PageLayout, Button, StatusBadge } from '../common/index.js';

const HubInsights = () => {
  const navigate = useNavigate();

  const [hubs, setHubs] = useState([]);
  const [selectedHubId, setSelectedHubId] = useState('');
  const [hubType, setHubType] = useState('sender');
  const [loading, setLoading] = useState(false);
  const [hubsLoading, setHubsLoading] = useState(true);

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_commission: 0, total_paid: 0, total_balance: 0 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payRecord, setPayRecord] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'CASH', transaction_ref: '', remarks: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [payFile, setPayFile] = useState(null);
  const [payFilePreview, setPayFilePreview] = useState(null);

  const paymentModes = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE'];

  useEffect(() => { fetchHubs(); }, []);

  const fetchHubs = async () => {
    try {
      setHubsLoading(true);
      const res = await locationHubService.getAllHubs({ all: 'true' });
      const raw = res.data;
      const hubData = raw?.hubs || raw?.data?.hubs || raw?.data || raw || [];
      const hubList = Array.isArray(hubData) ? hubData : [];
      setHubs(hubList);
      console.log('Hub sample:', hubList[0]); // remove after confirming fields
    } catch (err) {
      console.error('Failed to load hubs:', err);
      Alert.error('Failed to load location hubs');
    } finally {
      setHubsLoading(false);
    }
  };

  const fetchRecords = async () => {
    if (!selectedHubId) {
      Alert.error('Please select a location hub');
      return;
    }
    try {
      setLoading(true);
      setHasFetched(false);
      const res = await commissionService.getHubCommissionRecords(selectedHubId, hubType);
      const responseData = res.data;
      const data = responseData?.data || [];
      setRecords(Array.isArray(data) ? data : []);
      setTotal(responseData?.total || (Array.isArray(data) ? data.length : 0));
      setSummary(responseData?.summary || { total_commission: 0, total_paid: 0, total_balance: 0 });
      setHasFetched(true);
      setExpandedRow(null);
    } catch (err) {
      console.error('Failed to load hub records:', err);
      Alert.error(err.response?.data?.error || 'Failed to load hub commission records');
      setRecords([]);
      setTotal(0);
      setSummary({ total_commission: 0, total_paid: 0, total_balance: 0 });
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

  const getStatusLabel = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'PAID') return 'PAID';
    if (s === 'PARTIAL') return 'PARTIAL';
    return 'PENDING';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  const selectedHub = hubs.find(h => h.hub_id === selectedHubId);

  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  const openPayModal = (record) => {
    setPayRecord(record);
    setPayForm({ amount: '', payment_mode: 'CASH', transaction_ref: '', remarks: '' });
    setPayFile(null);
    setPayFilePreview(null);
    setPayModalOpen(true);
  };

  const closePayModal = () => {
    setPayModalOpen(false);
    setPayRecord(null);
    setPayFile(null);
    setPayFilePreview(null);
  };

  const handlePayFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { Alert.error('File must be under 5 MB'); return; }
    setPayFile(file);
    if (file.type.startsWith('image/')) {
      const r = new FileReader();
      r.onloadend = () => setPayFilePreview(r.result);
      r.readAsDataURL(file);
    } else {
      setPayFilePreview(null);
    }
  };

  const removePayFile = () => {
    setPayFile(null);
    setPayFilePreview(null);
    const el = document.getElementById('hub-pay-file');
    if (el) el.value = '';
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payRecord || !payForm.amount) return;
    const balance = parseFloat(payRecord.hub_commission_amount || 0) - parseFloat(payRecord.paid_amount || 0);
    if (parseFloat(payForm.amount) > balance) {
      Alert.error(`Amount exceeds remaining balance of ${formatCurrency(balance)}`);
      return;
    }
    setPayLoading(true);
    try {
      const recipientType = hubType === 'sender' ? 'SENDER_HUB' : 'RECEIVER_HUB';
      const hubLocation = selectedHub?.location || selectedHub?.hub_location || selectedHubId;
      const fd = new FormData();
      fd.append('commission_id',   payRecord.commission_id);
      fd.append('recipient_type',  recipientType);
      fd.append('recipient_id',    selectedHubId);
      fd.append('recipient_name',  `${hubType === 'sender' ? 'Sender' : 'Receiver'} Hub - ${hubLocation}`);
      fd.append('amount',          payForm.amount);
      fd.append('payment_date',    new Date().toISOString().split('T')[0]);
      fd.append('payment_mode',    payForm.payment_mode);
      if (payForm.transaction_ref) fd.append('transaction_ref', payForm.transaction_ref);
      if (payForm.remarks)         fd.append('remarks',         payForm.remarks);
      if (payFile)                 fd.append('file_attachment',  payFile);
      await commissionPaymentService.createPayment(fd);
      Alert.success('Payment recorded successfully!');
      closePayModal();
      fetchRecords();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <>
      <PageLayout
        title="Hub Insights"
        icon={MapPin}
        actions={
          <Button variant="outline" icon={DollarSign} onClick={() => navigate('/commissions')}>
            Back to Commissions
          </Button>
        }
      >
                {/* Filter Bar */}
                <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 items-end">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Location Hub</label>
                      {hubsLoading ? (
                        <div className="w-full px-4 py-2 border border-gray-300 rounded-sm text-sm text-gray-400">Loading hubs...</div>
                      ) : (
                        <select value={selectedHubId} onChange={(e) => setSelectedHubId(e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm font-medium text-tertiary">
                          <option value="">Select a hub...</option>
                          {hubs.map((hub, idx) => (
                            <option key={hub.hub_id || idx} value={hub.hub_id}>
                              {hub.location || hub.hub_location || hub.name || hub.hub_id} ({hub.hub_id})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Hub Type</label>
                      <div className="flex rounded-sm border border-gray-300 overflow-hidden">
                        <button type="button" onClick={() => setHubType('sender')}
                          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${hubType === 'sender' ? 'bg-buttonBg text-buttonTxt' : 'bg-white text-tertiary hover:bg-gray-50'}`}>
                          Sender
                        </button>
                        <button type="button" onClick={() => setHubType('receiver')}
                          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${hubType === 'receiver' ? 'bg-buttonBg text-buttonTxt' : 'bg-white text-tertiary hover:bg-gray-50'}`}>
                          Receiver
                        </button>
                      </div>
                    </div>
                    <div>
                      <Button onClick={fetchRecords} disabled={!selectedHubId || loading} icon={Search} className="w-full">
                        {loading ? 'Searching...' : 'Search'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Loading */}
                {loading && (
                  <div className="bg-white p-8 rounded-sm shadow-sm text-center mb-6">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-sm text-gray-600">Loading hub commission data...</p>
                  </div>
                )}

                {/* Results */}
                {!loading && hasFetched && (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white rounded-sm shadow-sm p-4 border-l-4 border-brand">
                        <p className="text-xs text-gray-500 font-medium mb-1">Total Commission</p>
                        <p className="text-xl font-bold text-secondary">{formatCurrency(summary.total_commission)}</p>
                      </div>
                      <div className="bg-white rounded-sm shadow-sm p-4 border-l-4 border-green-500">
                        <p className="text-xs text-gray-500 font-medium mb-1">Total Paid</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_paid)}</p>
                      </div>
                      <div className="bg-white rounded-sm shadow-sm p-4 border-l-4 border-red-500">
                        <p className="text-xs text-gray-500 font-medium mb-1">Total Balance</p>
                        <p className="text-xl font-bold text-red-600">{formatCurrency(summary.total_balance)}</p>
                      </div>
                      <div className="bg-white rounded-sm shadow-sm p-4 border-l-4 border-gray-400">
                        <p className="text-xs text-gray-500 font-medium mb-1">Total Records</p>
                        <p className="text-xl font-bold text-secondary">{total}</p>
                      </div>
                      <div className="bg-white rounded-sm shadow-sm p-4 border-l-4 border-purple-500">
                        <p className="text-xs text-gray-500 font-medium mb-1">Hub Location</p>
                        <p className="text-lg font-bold text-secondary truncate">{selectedHub?.location || selectedHub?.hub_location || selectedHubId}</p>
                      </div>
                      <div className="bg-white rounded-sm shadow-sm p-4 border-l-4 border-amber-500">
                        <p className="text-xs text-gray-500 font-medium mb-1">Hub Type</p>
                        <p className="text-lg font-bold text-secondary capitalize">{hubType}</p>
                      </div>
                    </div>

                    {/* Commission Records Table */}
                    <div className="bg-white rounded-sm shadow-sm hidden md:block">
                      {records.length === 0 ? (
                        <div className="p-8 text-center text-gray-600">No commission records found for this hub.</div>
                      ) : (
                        <div className="w-full overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="bg-primary text-tertiary">
                                <th className="px-3 py-3 text-left text-xs font-semibold w-8"></th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">COMM. ID</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">ML SHEET</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">HUB %</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">COMMISSION</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">FREIGHT</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">SHIPMENTS</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">PAID</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">STATUS</th>
                                <th className="px-3 py-3 text-center text-xs font-semibold">ACTION</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {records.map((rec) => {
                                const isExpanded = expandedRow === rec.commission_id;
                                const status = getStatusLabel(rec.payment_status);
                                const agents = Array.isArray(rec.agents) ? rec.agents : [];
                                const payments = Array.isArray(rec.payments) ? rec.payments : [];
                                return (
                                  <React.Fragment key={rec.commission_id}>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                      <td className="px-3 py-3">
                                        <button onClick={() => toggleRow(rec.commission_id)} className="text-gray-500 hover:text-gray-700">
                                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                        </button>
                                      </td>
                                      <td className="px-3 py-3 text-center text-xs">
                                        <button onClick={() => navigate(`/commissions/view/${rec.commission_id}`)} className="text-tertiary hover:underline font-semibold">{rec.commission_id}</button>
                                      </td>
                                      <td className="px-3 py-3 text-gray-700 text-center text-xs">{rec.ml_sheet_id}</td>
                                      <td className="px-3 py-3 text-gray-700 text-center text-xs font-semibold">{parseFloat(rec.hub_commission_percent || 0).toFixed(1)}%</td>
                                      <td className="px-3 py-3 text-gray-700 text-center text-xs font-semibold">{formatCurrency(rec.hub_commission_amount)}</td>
                                      <td className="px-3 py-3 text-gray-700 text-center text-xs">{formatCurrency(rec.hub_total_freight)}</td>
                                      <td className="px-3 py-3 text-gray-700 text-center text-xs">{rec.hub_total_shipments || 0}</td>
                                      <td className="px-3 py-3 text-gray-700 text-center text-xs font-semibold">{formatCurrency(rec.paid_amount)}</td>
                                      <td className="px-3 py-3 text-center text-xs">
                                        <StatusBadge status={rec.payment_status || 'PENDING'} />
                                      </td>
                                      <td className="px-3 py-3 text-center text-xs">
                                        {status !== 'PAID' && (
                                          <Button size="sm" onClick={() => openPayModal(rec)}>
                                            Quick Pay
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                    {isExpanded && (
                                      <tr>
                                        <td colSpan={10} className="bg-gray-50 px-6 py-4">
                                          <div className="space-y-4">
                                            {agents.length > 0 && (
                                              <div>
                                                <h4 className="text-xs font-semibold text-secondary mb-2">{hubType === 'sender' ? 'Agent Breakdown' : 'User Breakdown'}</h4>
                                                <table className="w-full text-xs border border-gray-200 rounded-sm">
                                                  <thead>
                                                    <tr className="bg-gray-100">
                                                      <th className="px-3 py-2 text-left font-semibold text-tertiary">{hubType === 'sender' ? 'Agent Name' : 'User Name'}</th>
                                                      <th className="px-3 py-2 text-left font-semibold text-tertiary">City</th>
                                                      {hubType === 'sender' && <th className="px-3 py-2 text-center font-semibold text-tertiary">Shipments</th>}
                                                      {hubType === 'sender' && <th className="px-3 py-2 text-right font-semibold text-tertiary">Freight</th>}
                                                      <th className="px-3 py-2 text-center font-semibold text-tertiary">Comm. %</th>
                                                      <th className="px-3 py-2 text-right font-semibold text-tertiary">Comm. Amount</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-gray-200">
                                                    {agents.map((a, i) => {
                                                      const agentPct = parseFloat(a.agent_commission_percent || 0);
                                                      const hubAmt = parseFloat(rec.hub_commission_amount || 0);
                                                      const agentAmt = (hubAmt * agentPct) / 100;
                                                      return (
                                                        <tr key={a.agent_id || i}>
                                                          <td className="px-3 py-2">{a.agent_name || a.agent_id} <span className="text-gray-400">({a.agent_id})</span></td>
                                                          <td className="px-3 py-2 text-gray-500">{a.agent_city || '-'}</td>
                                                          {hubType === 'sender' && <td className="px-3 py-2 text-center">{a.booked_shipments || 0}</td>}
                                                          {hubType === 'sender' && <td className="px-3 py-2 text-right font-semibold">{formatCurrency(a.total_freight)}</td>}
                                                          <td className="px-3 py-2 text-center">{agentPct.toFixed(1)}%</td>
                                                          <td className="px-3 py-2 text-right font-semibold">{formatCurrency(agentAmt)}</td>
                                                        </tr>
                                                      );
                                                    })}
                                                  </tbody>
                                                </table>
                                              </div>
                                            )}
                                            {payments.length > 0 ? (
                                              <div>
                                                <h4 className="text-xs font-semibold text-secondary mb-2">Payment History</h4>
                                                <table className="w-full text-xs border border-gray-200 rounded-sm">
                                                  <thead><tr className="bg-gray-100"><th className="px-3 py-2 text-left font-semibold text-tertiary">Payment ID</th><th className="px-3 py-2 text-center font-semibold text-tertiary">Amount</th><th className="px-3 py-2 text-center font-semibold text-tertiary">Date</th><th className="px-3 py-2 text-center font-semibold text-tertiary">Mode</th><th className="px-3 py-2 text-center font-semibold text-tertiary">Txn Ref</th><th className="px-3 py-2 text-left font-semibold text-tertiary">Remarks</th></tr></thead>
                                                  <tbody className="divide-y divide-gray-200">
                                                    {payments.map((p, i) => (
                                                      <tr key={p.payment_id || i}><td className="px-3 py-2">{p.payment_id}</td><td className="px-3 py-2 text-center font-semibold text-green-600">{formatCurrency(p.amount)}</td><td className="px-3 py-2 text-center">{formatDate(p.payment_date)}</td><td className="px-3 py-2 text-center">{(p.payment_mode || '').replace(/_/g, ' ')}</td><td className="px-3 py-2 text-center">{p.transaction_ref || '-'}</td><td className="px-3 py-2 text-gray-500">{p.remarks || '-'}</td></tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            ) : (
                                              <p className="text-xs text-gray-500">No payments recorded yet.</p>
                                            )}
                                            {agents.length === 0 && payments.length === 0 && <p className="text-xs text-gray-500">No additional details available.</p>}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {records.length === 0 ? (
                        <div className="bg-white p-8 rounded-sm shadow-sm text-center text-gray-600">No commission records found.</div>
                      ) : records.map((rec) => {
                        const isExpanded = expandedRow === rec.commission_id;
                        const status = getStatusLabel(rec.payment_status);
                        const agents = Array.isArray(rec.agents) ? rec.agents : [];
                        const payments = Array.isArray(rec.payments) ? rec.payments : [];
                        return (
                          <div key={rec.commission_id} className="bg-white rounded-sm shadow-sm p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h3 className="font-bold text-secondary text-sm">{rec.commission_id}</h3>
                                <p className="text-xs text-gray-500">ML Sheet: {rec.ml_sheet_id}</p>
                              </div>
                              <StatusBadge status={rec.payment_status || 'PENDING'} />
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                              <div><span className="text-gray-500">Hub %:</span> <span className="font-semibold">{parseFloat(rec.hub_commission_percent || 0).toFixed(1)}%</span></div>
                              <div><span className="text-gray-500">Commission:</span> <span className="font-semibold">{formatCurrency(rec.hub_commission_amount)}</span></div>
                              <div><span className="text-gray-500">Paid:</span> <span className="font-semibold text-green-600">{formatCurrency(rec.paid_amount)}</span></div>
                              <div><span className="text-gray-500">Shipments:</span> <span className="font-semibold">{rec.hub_total_shipments || 0}</span></div>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => toggleRow(rec.commission_id)} className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 text-xs font-medium flex items-center justify-center gap-1">
                                {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Details
                              </button>
                              {status !== 'PAID' && (
                                <Button size="sm" onClick={() => openPayModal(rec)} className="flex-1">
                                  Quick Pay
                                </Button>
                              )}
                            </div>
                            {isExpanded && (
                              <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                                {agents.length > 0 && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-secondary mb-1">{hubType === 'sender' ? 'Agents' : 'Users'}</h4>
                                    {agents.map((a, i) => {
                                      const agentPct = parseFloat(a.agent_commission_percent || 0);
                                      const hubAmt = parseFloat(rec.hub_commission_amount || 0);
                                      const agentAmt = (hubAmt * agentPct) / 100;
                                      return (
                                        <div key={a.agent_id || i} className="text-xs py-1.5 border-b border-gray-100 last:border-0">
                                          <div className="flex justify-between">
                                            <span>{a.agent_name || a.agent_id} <span className="text-gray-400">({a.agent_id})</span></span>
                                            <span className="font-semibold">{formatCurrency(agentAmt)}</span>
                                          </div>
                                          <div className="flex gap-3 text-gray-500 mt-0.5">
                                            <span>{a.agent_city || '-'}</span>
                                            <span>{agentPct.toFixed(1)}%</span>
                                            {hubType === 'sender' && <span>{a.booked_shipments || 0} shipments</span>}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {payments.length > 0 && <div><h4 className="text-xs font-semibold text-secondary mb-1">Payments</h4>{payments.map((p, i) => <div key={p.payment_id || i} className="flex justify-between text-xs py-1 border-b border-gray-100 last:border-0"><span>{p.payment_id}</span><span className="font-semibold text-green-600">{formatCurrency(p.amount)}</span></div>)}</div>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {!loading && !hasFetched && (
                  <div className="bg-white p-8 rounded-sm shadow-sm text-center">
                    <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Select a Hub to Get Started</h3>
                    <p className="text-sm text-gray-500">Choose a location hub and hub type, then click Search to view commission records.</p>
                  </div>
                )}
      </PageLayout>

      {/* Quick Pay Modal */}
      {payModalOpen && payRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-secondary">Quick Pay — {hubType === 'sender' ? 'Sender' : 'Receiver'} Hub</h2>
              <button onClick={closePayModal} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handlePaySubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-secondary mb-1">Commission ID</label><input type="text" value={payRecord.commission_id} readOnly className="w-full px-4 py-2 border border-gray-200 rounded-sm bg-gray-50 text-sm text-gray-600" /></div>
                <div><label className="block text-sm font-medium text-secondary mb-1">Hub ID</label><input type="text" value={selectedHubId} readOnly className="w-full px-4 py-2 border border-gray-200 rounded-sm bg-gray-50 text-sm text-gray-600" /></div>
                <div><label className="block text-sm font-medium text-secondary mb-1">Hub Location</label><input type="text" value={selectedHub?.location || selectedHub?.hub_location || selectedHubId} readOnly className="w-full px-4 py-2 border border-gray-200 rounded-sm bg-gray-50 text-sm text-gray-600" /></div>
                <div><label className="block text-sm font-medium text-secondary mb-1">Current Balance</label><input type="text" value={formatCurrency(parseFloat(payRecord.hub_commission_amount || 0) - parseFloat(payRecord.paid_amount || 0))} readOnly className="w-full px-4 py-2 border border-gray-200 rounded-sm bg-gray-50 text-sm font-semibold text-red-600" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-secondary mb-1">Amount <span className="text-red-500">*</span></label><input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder="Enter amount" className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm" required /></div>
                <div><label className="block text-sm font-medium text-secondary mb-1">Payment Mode <span className="text-red-500">*</span></label><select value={payForm.payment_mode} onChange={(e) => setPayForm({ ...payForm, payment_mode: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm">{paymentModes.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}</select></div>
              </div>
              <div><label className="block text-sm font-medium text-secondary mb-1">Transaction Reference</label><input type="text" value={payForm.transaction_ref} onChange={(e) => setPayForm({ ...payForm, transaction_ref: e.target.value })} placeholder="UTR / Cheque no." className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm" /></div>
              <div><label className="block text-sm font-medium text-secondary mb-1">Remarks</label><textarea value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} rows="2" placeholder="Optional remarks..." className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm" /></div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Payment Proof (Optional)</label>
                <input id="hub-pay-file" type="file" accept="image/*,.pdf" onChange={handlePayFileChange} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm" />
                <p className="text-xs text-gray-500 mt-1">Images or PDF, max 5 MB</p>
                {payFilePreview && (
                  <div className="mt-3 relative inline-block">
                    <img src={payFilePreview} alt="Preview" className="w-24 h-24 object-cover border rounded-md" />
                    <button type="button" onClick={removePayFile} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">×</button>
                  </div>
                )}
                {payFile && !payFilePreview && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                    📄 {payFile.name}
                    <button type="button" onClick={removePayFile} className="text-red-600 text-xs">Remove</button>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" loading={payLoading} className="flex-1">
                  {payLoading ? 'Processing...' : 'Submit Payment'}
                </Button>
                <Button type="button" variant="outline" onClick={closePayModal}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default HubInsights;
